import { AerDropdownMenu } from "@aerstudios/aerated-aerdropdown";
import { AerInputStructure } from "@aerstudios/aerated-aerinputstructure";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import React, { useEffect, useRef, useState } from "react";
import Check from "../../../common/Icons/Check";
import Label from "../../Label/Label";
import * as styles from "./SelectMulti.module.scss";

type TriggerProps = {
	selected: string[];
	toggleDropdown: () => void;
};

const Trigger = React.forwardRef(
	(
		{ selected, toggleDropdown }: TriggerProps,
		ref:
			| React.ForwardedRef<HTMLInputElement>
			| React.ForwardedRef<HTMLButtonElement>,
	) => {
		const buttonText =
			selected.length === 0 ? "Please select" : `${selected.length} selected`;

		return (
			<button
				ref={ref as React.ForwardedRef<HTMLButtonElement>}
				type="button"
				onClick={toggleDropdown}
				className={styles.selectButton}
			>
				<div className={styles.buttonContent}>
					<span>{buttonText}</span>

					<ChevronDownIcon className={styles.icon} aria-hidden="true" />
				</div>
			</button>
		);
	},
);
export interface SelectMultiProps {
	// The id of the select
	id: string;
	// The label for the select

	// The name of the select
	name: string;
	label: string;
	// The options for the select
	options: NDS.FormOption[];
	// The value of the selected option
	value: string[];
	// The function to call when an option is selected
	onChange: (value: string[]) => void;
	// The error message to display
	errorMessage?: string;

	// Whether the select is hidden
	hidden?: boolean;
}

export const SelectMulti: React.FC<SelectMultiProps> = ({
	label,
	hidden,
	errorMessage,
	options,
	value,
	onChange,
}) => {
	// Multi Select
	// Is a dropdown which allows the user to select a Multi option
	// Optionally allows the user to search to drill down to the options they want
	// The selected option is displayed in the dropdown trigger

	const [key, setKey] = useState(0);

	// This is a hack to force the radio group to re-render first time and load the incoming value.
	useEffect(() => {
		setKey((prev) => prev + 1);
	}, []);

	const [filteredItems, _setFilteredItems] = useState(() => options);

	const ref = useRef(null);
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<string[]>(value || []);

	const closeDropdown = (e: Event) => {
		// ensures that clicking on the trigger won't close the dropdown as you
		// try to open it
		if (ref.current && e.currentTarget === ref.current) {
			return;
		}

		setOpen(false);
	};

	const toggleDropdown = () => setOpen((v) => !v);

	const handleChange = (value: string) => (isChecked: boolean) => {
		setSelected((prevState) => {
			const prevSet = new Set(prevState);

			const verb = isChecked ? "add" : "delete";
			prevSet[verb](value);

			const nextState = [...prevSet];

			// sync checked status to the form
			onChange(nextState);

			return nextState;
		});
	};

	return (
		<AerInputStructure.Root
			className={styles.selectWrapper}
			errorMessage={errorMessage}
			hidden={hidden}
		>
			<Label id={label} label={label} name={label} />
			<AerDropdownMenu.Root
				key={key}
				open={open}
				onCloseAutoFocus={closeDropdown}
				onEscapeKeyDown={closeDropdown}
				onPointerDownOutside={closeDropdown}
				onFocusOutside={closeDropdown}
				onInteractOutside={closeDropdown}
				modal={true}
				className={styles.dropdown}
				align="start"
				alignOffset={0}
				trigger={
					<Trigger
						selected={selected}
						toggleDropdown={toggleDropdown}
						ref={ref}
					/>
				}
			>
				{filteredItems.map((answer, i) => (
					<AerDropdownMenu.CheckboxMenuItem
						key={answer.label}
						style={{
							borderTop: i > 0 && "1px solid #222",
						}}
						className={styles.checkboxMenuItem}
						onCheckedChange={handleChange(answer.value)}
						checked={selected.includes(answer.value)}
						checkIcon={
							<span className={styles.checkedIcon}>
								<Check
									height="14px"
									width="14px"
									className={styles.checkboxTick}
									aria-hidden="true"
									aria-label="checkbox tick"
								/>
							</span>
						}
					>
						<span className={styles.itemlabel}>{answer.label}</span>
					</AerDropdownMenu.CheckboxMenuItem>
				))}

				{filteredItems.length === 0 && (
					<AerDropdownMenu.CheckboxMenuItem disabled>
						No results found
					</AerDropdownMenu.CheckboxMenuItem>
				)}
			</AerDropdownMenu.Root>
			<AerInputStructure.ErrorMessage />
		</AerInputStructure.Root>
	);
};
